import { Controller } from "@hotwired/stimulus"
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode"

export default class extends Controller {
  static targets = ["camera", "manual", "cameraTab", "manualTab", "cameraSelect", "cameraStatus", "reader", "load", "start", "stop", "token", "location", "submit", "status", "recent"]
  static values = { url: String, labels: Object }

  connect() {
    this.connected = true
    this.busy = false
    this.starting = false
    this.loading = false
    this.scanner = null
    this.scans = []
    this.generation = (this.generation || 0) + 1
    this.cameraMode()
  }

  disconnect() {
    this.connected = false
    this.generation += 1
    // A start already in flight checks the generation and releases its camera.
    void this.stop()
    this.tokenTarget.value = ""
    this.locationTarget.value = ""
    this.statusTarget.replaceChildren()
    this.statusTarget.classList.add("hidden")
    this.scans = []
    this.renderRecent()
  }

  cameraMode() { this.setMode(false) }
  manualMode() { this.setMode(true); this.tokenTarget.focus() }

  setMode(manual) {
    this.cameraTarget.classList.toggle("hidden", manual)
    this.manualTarget.classList.toggle("hidden", !manual)
    this.cameraTabTarget.setAttribute("aria-pressed", String(!manual))
    this.manualTabTarget.setAttribute("aria-pressed", String(manual))
    if (manual) void this.stop()
  }

  async loadCameras() {
    if (this.loading || this.starting) return
    this.loading = true
    const generation = this.generation
    this.loadTarget.disabled = true
    this.cameraStatusTarget.textContent = this.labelsValue.loading
    try {
      await this.stop()
      if (!window.isSecureContext || !navigator.mediaDevices) throw new Error("camera unavailable")
      const cameras = await Html5Qrcode.getCameras()
      if (!this.connected || generation !== this.generation) return
      this.cameraSelectTarget.replaceChildren(...cameras.map((camera, index) => new Option(camera.label || `${this.labelsValue.camera} ${index + 1}`, camera.id)))
      this.cameraSelectTarget.disabled = !cameras.length
      this.startTarget.disabled = !cameras.length
      this.cameraStatusTarget.textContent = cameras.length ? this.labelsValue.scanning : this.labelsValue.no_camera
    } catch {
      if (this.connected && generation === this.generation) {
        this.cameraStatusTarget.textContent = this.labelsValue.camera_error
        this.startTarget.disabled = true
      }
    } finally {
      this.loading = false
      if (this.connected && generation === this.generation) this.loadTarget.disabled = false
    }
  }

  async start() {
    if (this.starting || this.scanner || !this.cameraSelectTarget.value) return
    this.starting = true
    this.startTarget.disabled = true
    this.loadTarget.disabled = true
    this.cameraSelectTarget.disabled = true
    const generation = this.generation
    const scanner = new Html5Qrcode(this.readerTarget.id)
    this.scanner = scanner
    try {
      await scanner.start(this.cameraSelectTarget.value, { fps: 8, qrbox: { width: 200, height: 200 } }, text => {
        if (this.connected && generation === this.generation && !this.busy) void this.validate(text)
      }, () => {})
      if (!this.connected || generation !== this.generation || this.cameraTarget.classList.contains("hidden")) {
        await this.releaseScanner(scanner)
        return
      }
      this.stopTarget.disabled = false
      this.cameraStatusTarget.textContent = this.labelsValue.scanning
    } catch {
      if (this.connected && generation === this.generation) this.cameraStatusTarget.textContent = this.labelsValue.camera_error
      await this.releaseScanner(scanner)
      if (this.scanner === scanner) this.scanner = null
      if (scanner.getState() === Html5QrcodeScannerState.NOT_STARTED) scanner.clear()
    } finally {
      if (this.connected && generation === this.generation) {
        this.starting = false
        this.startTarget.disabled = !!this.scanner
        this.loadTarget.disabled = !!this.scanner
        this.cameraSelectTarget.disabled = !!this.scanner
      }
    }
  }

  async stop() {
    await this.releaseScanner(this.scanner)
    if (this.connected) {
      this.stopTarget.disabled = true
      this.startTarget.disabled = !this.cameraSelectTarget.value || this.starting
      this.loadTarget.disabled = this.loading || this.starting
      this.cameraSelectTarget.disabled = !this.cameraSelectTarget.options.length
    }
  }

  async releaseScanner(scanner) {
    if (!scanner) return
    // start() resolves before isScanning becomes true; use the state machine
    // so switching modes immediately still releases the media stream.
    if (scanner.getState() === Html5QrcodeScannerState.NOT_STARTED) return
    this.releasing ||= new WeakSet()
    if (this.releasing.has(scanner)) return
    this.releasing.add(scanner)
    if (this.scanner === scanner) this.scanner = null
    try {
      await scanner.stop()
      scanner.clear()
    } catch {
      // Release tracks even if the library's DOM cleanup fails on navigation.
      this.readerTarget.querySelectorAll("video").forEach(video => {
        video.srcObject?.getTracks().forEach(track => track.stop())
      })
    }
  }

  submit(event) {
    event.preventDefault()
    const token = this.tokenTarget.value.trim()
    if (token) void this.validate(token)
  }

  async validate(token) {
    if (this.busy) return
    this.busy = true
    const generation = this.generation
    this.submitTarget.disabled = true
    this.showStatus(this.labelsValue.working)
    try {
      // Stop after a decoded QR, including a rejected QR. Staff explicitly
      // restart for the next scan, avoiding repeated requests for one code.
      await this.stop()
      const response = await fetch(this.urlValue, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
        body: JSON.stringify({ token, location: this.locationTarget.value.trim() })
      })
      const data = await response.json()
      if (!this.connected || generation !== this.generation) return
      if (!response.ok || !data.valid) {
        this.showStatus(data.error || this.labelsValue.errors.system)
        return
      }
      this.tokenTarget.value = ""
      this.showStatus(`${data.message} — ${data.member_name} · ${data.class_name} · ${data.class_time} · ${this.labelsValue.points}: ${data.points_earned}`)
      this.scans.unshift(data)
      this.scans = this.scans.slice(0, 10)
      this.renderRecent()
    } catch {
      if (this.connected && generation === this.generation) this.showStatus(this.labelsValue.network_error)
    } finally {
      if (generation === this.generation) {
        this.busy = false
        this.submitTarget.disabled = false
      }
    }
  }

  showStatus(text) {
    this.statusTarget.textContent = text
    this.statusTarget.classList.remove("hidden")
  }

  renderRecent() {
    const rows = this.scans.map(scan => {
      const row = document.createElement("li")
      row.className = "py-3"
      row.textContent = `${scan.member_name} · ${scan.class_name} · ${scan.class_time} · ${this.labelsValue.points}: ${scan.points_earned}`
      return row
    })
    if (!rows.length) {
      const empty = document.createElement("li")
      empty.textContent = this.labelsValue.empty
      rows.push(empty)
    }
    this.recentTarget.replaceChildren(...rows)
  }
}
